<template>
  <div class="review-dashboard-page">
    <AppHeader />
    <div class="dashboard-container">
      <!-- ページヘッダー -->
      <header class="dashboard-header">
        <div class="header-titles">
          <p class="dashboard-kicker">旅行の振り返り & 好み分析</p>
          <h1 class="dashboard-title">評価ダッシュボード</h1>
          <p class="dashboard-desc">
            これまでの旅行評価を集計・分析し、あなたやグループの好みに合った次の旅先をご提案します。
          </p>
        </div>
      </header>

      <!-- 全体フロー図（STEP 4がアクティブ） -->
      <TripWorkflowDiagram :current-step="4" />

      <!-- 1. 未評価のしおりを評価しませんか？（要件1） -->
      <section class="unreviewed-section" aria-labelledby="unreviewed-title">
        <div class="section-badge-header">
          <div class="section-title-wrap">
            <span class="pulse-badge">未評価</span>
            <h2 id="unreviewed-title" class="section-title">評価を行っていないしおりを評価しませんか？</h2>
          </div>
          <span class="section-badge-hint">★ 評価を記録すると提案精度がUPします</span>
        </div>
        <p class="section-subtext">
          旅行後の感想や満足度を記録して、次の旅行計画やおすすめ提案に役立てましょう。
        </p>

        <!-- 未評価のしおりがある場合 -->
        <div v-if="unreviewedTrips.length > 0" class="unreviewed-grid">
          <article
            v-for="trip in unreviewedTrips"
            :key="trip.SHIORI_NOV"
            class="unreviewed-card"
          >
            <div class="unreviewed-media">
              <img
                v-if="trip.IMAGE_URL"
                :src="trip.IMAGE_URL"
                alt=""
                class="trip-thumb"
              />
              <div v-else class="unreviewed-placeholder">
                <IconSvg name="plane-solid-full" size="md" />
              </div>
            </div>
            <div class="unreviewed-content">
              <h3 class="unreviewed-trip-title">{{ trip.SUBJECT_NMV || '旅行のしおり' }}</h3>
              <p class="unreviewed-date">
                <IconSvg name="calendar-solid-full" size="xs" />
                {{ formatDateRange(trip.TRIP_FROM, trip.TRIP_TO) }}
              </p>
              <div class="unreviewed-action">
                <button
                  type="button"
                  class="btn btn-primary btn-sm btn-rate-shiori"
                  @click="openRateModalForShiori(trip)"
                >
                  <IconSvg name="star-solid-full" size="xs" />
                  <span>しおりを評価する</span>
                </button>
              </div>
            </div>
          </article>
        </div>

        <!-- 未評価のしおりがない場合 -->
        <div v-else class="all-reviewed-box">
          <div class="reviewed-icon">🎉</div>
          <div class="reviewed-text">
            <strong>すべてのしおりが評価済みです！</strong>
            <p>過去の旅行評価がすべて反映され、最適な旅行先提案に活用されています。</p>
          </div>
        </div>
      </section>

      <!-- 2. 旅行評価の集計（平均・中央値・最高値・最低値・満足度など） -->
      <section class="stats-section" aria-labelledby="stats-title">
        <div class="stats-header">
          <div>
            <span class="stats-kicker">スコア分析</span>
            <h2 id="stats-title" class="section-title">旅行評価の多角的な集計分析</h2>
          </div>

          <div class="header-actions-row">
            <!-- 手動旅行評価を追加（要件2） -->
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-add-manual"
              @click="openNewManualModal"
            >
              <IconSvg name="plus-solid-full" size="xs" />
              <span>しおり外の旅行を記録・学習</span>
            </button>

            <!-- メンバー切り替えフィルター -->
            <div class="member-filter-wrap">
              <label for="member-select" class="filter-label">
                <IconSvg name="user-solid-full" size="xs" />
                <span>参加者:</span>
              </label>
              <select
                id="member-select"
                v-model="selectedMember"
                class="member-select"
              >
                <option value="all">全員のデータ</option>
                <option
                  v-for="m in uniqueMembers"
                  :key="m"
                  :value="m"
                >
                  {{ m }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- 主要サマリーKPIカード（最高・最低をクリックでカード表示連動） -->
        <div class="metrics-kpi-grid">
          <div
            class="kpi-card"
            :class="{ 'is-selected': activeMetricType === 'mean' }"
            @click="selectMetric('mean')"
            role="button"
            tabindex="0"
          >
            <span class="kpi-label">📊 平均値</span>
            <div class="kpi-value">
              <strong>{{ overallStats.mean.toFixed(1) }}</strong>
              <span class="kpi-max">/ 5.0</span>
            </div>
            <span class="kpi-caption">全旅行の標準満足度</span>
          </div>

          <div
            class="kpi-card"
            :class="{ 'is-selected': activeMetricType === 'median' }"
            @click="selectMetric('median')"
            role="button"
            tabindex="0"
          >
            <span class="kpi-label">🎯 中央値</span>
            <div class="kpi-value">
              <strong>{{ overallStats.median.toFixed(1) }}</strong>
              <span class="kpi-max">/ 5.0</span>
            </div>
            <span class="kpi-caption">実感・代表スコア</span>
          </div>

          <div
            class="kpi-card highlight-card-gold"
            :class="{ 'is-selected': activeMetricType === 'max' }"
            @click="selectMetric('max')"
            role="button"
            tabindex="0"
          >
            <span class="kpi-label">🏆 最高評価</span>
            <div class="kpi-value highlight-gold">
              <strong>{{ overallStats.max.toFixed(1) }}</strong>
              <span class="kpi-max">/ 5.0</span>
            </div>
            <span class="kpi-caption">クリックで該当しおりを表示 ▾</span>
          </div>

          <div
            class="kpi-card highlight-card-warn"
            :class="{ 'is-selected': activeMetricType === 'min' }"
            @click="selectMetric('min')"
            role="button"
            tabindex="0"
          >
            <span class="kpi-label">⚠️ 最低評価 / 改善点</span>
            <div class="kpi-value">
              <strong>{{ overallStats.min.toFixed(1) }}</strong>
              <span class="kpi-max">/ 5.0</span>
            </div>
            <span class="kpi-caption">クリックで該当しおりを表示 ▾</span>
          </div>

          <div class="kpi-card kpi-meta-card">
            <span class="kpi-label">🌟 高評価率 (★4以上)</span>
            <div class="kpi-value">
              <strong>{{ satisfactionRate }}%</strong>
            </div>
            <span class="kpi-caption">満足した旅行の割合</span>
          </div>

          <div class="kpi-card kpi-meta-card">
            <span class="kpi-label">🗺️ 総旅行記録数</span>
            <div class="kpi-value">
              <strong>{{ filteredReviews.length }}</strong>
              <span class="kpi-max">件</span>
            </div>
            <span class="kpi-caption">しおり評価＋手動学習</span>
          </div>
        </div>

        <!-- 3. カテゴリー別スコア詳細 -->
        <div class="category-detail-card">
          <div class="category-card-header">
            <div class="category-card-titles">
              <h3 class="category-card-title">カテゴリー別スコア詳細</h3>
              <p class="category-card-desc">
                現在表示中: <strong>{{ metricTypeLabels[activeMetricType] }}</strong>（上のカードまたは右のボタンで指標を切り替えられます）
              </p>
            </div>
            <div class="metric-switch-pills">
              <button
                v-for="(label, key) in metricTypeLabels"
                :key="key"
                type="button"
                class="metric-pill-btn"
                :class="{ active: activeMetricType === key }"
                @click="selectMetric(key)"
              >
                {{ label }}
              </button>
            </div>
          </div>

          <div class="category-bars-grid">
            <div
              v-for="cat in categoryMetrics"
              :key="cat.key"
              class="category-stat-row"
            >
              <div class="cat-stat-meta">
                <span class="cat-name">{{ cat.icon }} {{ cat.label }}</span>
                <span class="cat-score-bold">
                  {{ cat[activeMetricType] > 0 ? cat[activeMetricType].toFixed(1) : '-' }}
                  <small v-if="cat[activeMetricType] > 0">/ 5.0</small>
                </span>
              </div>
              <div class="stat-bar-track">
                <div
                  class="stat-bar-fill"
                  :style="{ width: `${(cat[activeMetricType] / 5) * 100}%` }"
                ></div>
              </div>
              <div class="cat-stat-sub-info">
                <span>平均: {{ cat.mean.toFixed(1) }}</span>
                <span>中央値: {{ cat.median.toFixed(1) }}</span>
                <span>最高: {{ cat.max.toFixed(1) }}</span>
                <span>最低: {{ cat.min.toFixed(1) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. 指標連動：該当する旅行・しおりカード一覧（最高評価/最低評価/全件表示） -->
        <div class="active-metric-trips-panel">
          <div class="metric-trips-header">
            <div class="metric-trips-titles">
              <span class="metric-trips-kicker">{{ metricHeaderKicker }}</span>
              <h3 class="metric-trips-title">{{ metricHeaderTitle }}</h3>
              <p class="metric-trips-desc">
                カードをクリックすると、評価スコアや感想メモを直接更新できます。
              </p>
            </div>
            <div class="metric-switch-pills">
              <button
                v-for="(label, key) in metricTypeLabels"
                :key="key"
                type="button"
                class="metric-pill-btn"
                :class="{ active: activeMetricType === key }"
                @click="selectMetric(key)"
              >
                {{ label }}
              </button>
            </div>
          </div>

          <!-- 該当旅行カードグリッド -->
          <div v-if="filteredTripsForMetric.length > 0" class="metric-trips-grid">
            <article
              v-for="trip in filteredTripsForMetric"
              :key="trip.id"
              class="metric-trip-card"
              @click="openEditModalForTrip(trip)"
              role="button"
              tabindex="0"
              :title="'クリックして評価を更新'"
            >
              <div class="metric-trip-card-header">
                <div class="metric-trip-info">
                  <strong class="metric-trip-name">{{ trip.title }}</strong>
                  <span v-if="trip.date" class="metric-trip-date">📅 {{ trip.date }}</span>
                  <span v-if="trip.isManual" class="badge-source-manual">手動学習</span>
                  <span v-else class="badge-source-shiori">しおり</span>
                </div>
                <div class="metric-trip-score-badge" :class="{ 'is-gold': trip.overallScore >= 4.8 }">
                  ★ {{ Number(trip.overallScore).toFixed(1) }}
                </div>
              </div>

              <p v-if="trip.memo" class="metric-trip-memo">「{{ trip.memo }}」</p>

              <!-- カテゴリー別スコアミニタグ -->
              <div class="metric-trip-categories">
                <span v-if="trip.lodgingScore" class="trip-cat-pill">🏨 宿 {{ trip.lodgingScore }}</span>
                <span v-if="trip.foodScore" class="trip-cat-pill">🍽️ 食 {{ trip.foodScore }}</span>
                <span v-if="trip.sightseeingScore" class="trip-cat-pill">⛩️ 観 {{ trip.sightseeingScore }}</span>
                <span v-if="trip.activityScore" class="trip-cat-pill">🏄 ア {{ trip.activityScore }}</span>
                <span v-if="trip.transportScore" class="trip-cat-pill">🚆 交 {{ trip.transportScore }}</span>
              </div>

              <div class="metric-trip-card-footer" @click.stop>
                <button
                  type="button"
                  class="btn-edit-trip-score"
                  @click="openEditModalForTrip(trip)"
                >
                  <span>✏️ 評価を更新する</span>
                </button>
                <router-link
                  v-if="trip.shioriId"
                  :to="`/shiori/${trip.shioriId}`"
                  class="btn-view-shiori-link"
                >
                  <span>しおりを見る →</span>
                </router-link>
              </div>
            </article>
          </div>

          <div v-else class="empty-metric-trips">
            <p>該当する旅行データがありません。</p>
          </div>
        </div>

        <!-- 手動で追加された過去の旅行評価一覧 -->
        <div v-if="manualReviews.length > 0" class="manual-reviews-section">
          <div class="manual-header">
            <span class="manual-title">📝 追加・学習された過去の旅行ログ ({{ manualReviews.length }}件)</span>
            <span class="manual-hint">クリックして編集・更新ができます</span>
          </div>
          <div class="manual-reviews-list">
            <div
              v-for="m in manualReviews"
              :key="m.id"
              class="manual-review-item"
              @click="openEditModalForTrip(m)"
              role="button"
              tabindex="0"
            >
              <div class="manual-item-main">
                <div class="manual-item-top">
                  <strong class="manual-item-title">{{ m.title }}</strong>
                  <span v-if="m.date" class="manual-item-date">📅 {{ m.date }}</span>
                  <span class="manual-item-score">★ {{ Number(m.overallScore).toFixed(1) }}</span>
                </div>
                <p v-if="m.memo" class="manual-item-memo">{{ m.memo }}</p>
                <div class="manual-item-tags">
                  <span class="cat-mini-tag" v-if="m.lodgingScore">🏨 宿{{ m.lodgingScore }}</span>
                  <span class="cat-mini-tag" v-if="m.foodScore">🍽️ 食{{ m.foodScore }}</span>
                  <span class="cat-mini-tag" v-if="m.sightseeingScore">⛩️ 観{{ m.sightseeingScore }}</span>
                  <span class="cat-mini-tag" v-if="m.activityScore">🏄 ア{{ m.activityScore }}</span>
                  <span class="cat-mini-tag" v-if="m.transportScore">🚆 交{{ m.transportScore }}</span>
                  <span v-if="m.members && m.members.length" class="cat-mini-tag member-tag">
                    👥 {{ m.members.join(', ') }}
                  </span>
                </div>
              </div>
              <div class="manual-item-actions" @click.stop>
                <button
                  type="button"
                  class="btn-icon-edit"
                  @click="openEditModalForTrip(m)"
                  title="評価を編集"
                >
                  ✏️
                </button>
                <button
                  type="button"
                  class="btn-delete-manual"
                  @click="removeManualReview(m.id)"
                  title="この旅行評価を削除"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 評価に基づく推奨旅行先 & しおり作成動線 -->
      <section class="recommend-section" aria-labelledby="recommend-title">
        <div class="recommend-header">
          <div>
            <span class="recommend-kicker">あなたへのご提案</span>
            <h2 id="recommend-title" class="section-title">評価傾向からおすすめする次の旅行先</h2>
            <p class="section-subtext">
              高評価だったカテゴリー（{{ topCategoryLabel }}）の傾向に合わせて厳選したプランです。ワンクリックですぐにしおりを作成できます。
            </p>
          </div>
        </div>

        <div class="recommend-grid">
          <article
            v-for="plan in recommendedDestinations"
            :key="plan.id"
            class="recommend-card"
          >
            <div class="recommend-image-wrap">
              <img :src="plan.imageUrl" :alt="plan.title" class="recommend-img" />
              <span class="recommend-tag">{{ plan.tag }}</span>
            </div>
            <div class="recommend-body">
              <h3 class="recommend-plan-title">{{ plan.title }}</h3>
              <p class="recommend-plan-desc">{{ plan.description }}</p>
              <div class="recommend-meta">
                <span><IconSvg name="calendar-solid-full" size="xs" /> {{ plan.days }}日間</span>
                <span><IconSvg name="location-dot-solid-full" size="xs" /> {{ plan.area }}</span>
              </div>
              <button
                type="button"
                class="btn btn-primary w-full btn-create-from-rec"
                @click="openCreateModalWithPrefill(plan)"
              >
                <IconSvg name="plus-solid-full" size="xs" />
                <span>この内容でしおりを作成</span>
              </button>
            </div>
          </article>
        </div>
      </section>
    </div>

    <!-- 旅行評価追加・編集モーダル（更新対応） -->
    <ManualTripReviewModal
      v-model="showManualModal"
      :initial-data="activeEditingReview"
      @save="onManualReviewSaved"
    />

    <!-- しおり作成モーダル -->
    <CreateShioriModal
      v-model="showCreateModal"
      :prefill-data="activePrefillData"
      @create="onShioriCreated"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import AppHeader from "@/components/AppHeader.vue";
import IconSvg from "@/components/IconSvg.vue";
import TripWorkflowDiagram from "@/components/TripWorkflowDiagram.vue";
import CreateShioriModal from "@/components/CreateShioriModal.vue";
import ManualTripReviewModal from "@/components/ManualTripReviewModal.vue";
import { useShioriStore } from "@/store/shiori-store.js";
import { useTripReviewStore } from "@/store/trip-review-store.js";
import { useAuthStore } from "@/store/authStore.js";

const router = useRouter();
const shioriStore = useShioriStore();
const tripReviewStore = useTripReviewStore();
const authStore = useAuthStore();

const selectedMember = ref("all");
const reviewsData = ref([]);
const manualReviews = ref([]);
const showCreateModal = ref(false);
const showManualModal = ref(false);
const activeEditingReview = ref(null);
const activePrefillData = ref({});
const activeMetricType = ref("mean"); // 'mean' | 'median' | 'max' | 'min'

const metricTypeLabels = {
  mean: "平均値",
  median: "中央値",
  max: "最高評価",
  min: "最低評価",
};

const selectMetric = (type) => {
  activeMetricType.value = type;
};

// 1. 未評価のしおり一覧
const unreviewedTrips = computed(() => {
  const shioris = shioriStore.shioris || [];
  return shioris.filter((s) => {
    const hasReview =
      tripReviewStore.reviewSummaries[s.SHIORI_NOV]?.overallScore !== null &&
      tripReviewStore.reviewSummaries[s.SHIORI_NOV]?.overallScore !== undefined;
    return !hasReview;
  });
});

// ユニークなメンバー一覧
const uniqueMembers = computed(() => {
  const membersSet = new Set();
  (shioriStore.shioris || []).forEach((s) => {
    if (Array.isArray(s.members)) {
      s.members.forEach((m) => {
        const name = typeof m === "string" ? m : m.MEMBER_NAME;
        if (name) membersSet.add(name);
      });
    }
  });
  manualReviews.value.forEach((m) => {
    if (Array.isArray(m.members)) {
      m.members.forEach((name) => membersSet.add(name));
    }
  });
  if (authStore.user?.username) {
    membersSet.add(authStore.user.username);
  }
  return Array.from(membersSet);
});

// 統合された全レビュー一覧（しおりレビュー＋手動学習レビュー）
const allCombinedReviews = computed(() => {
  return [...reviewsData.value, ...manualReviews.value];
});

// 選択メンバーでフィルタリングされたレビュー一覧
const filteredReviews = computed(() => {
  if (selectedMember.value === "all") {
    return allCombinedReviews.value;
  }
  return allCombinedReviews.value.filter((r) => {
    return r.members?.includes(selectedMember.value);
  });
});

// 指標（最高値・最低値・平均等）に連動して表示する旅行一覧
const filteredTripsForMetric = computed(() => {
  const list = [...filteredReviews.value];
  if (list.length === 0) return [];

  if (activeMetricType.value === "max") {
    // 最高評価スコアの旅行（または4.8以上の高評価旅行を降順で）
    const maxScore = Math.max(...list.map((r) => r.overallScore || 0));
    return list.filter((r) => r.overallScore >= Math.min(maxScore, 4.8)).sort((a, b) => b.overallScore - a.overallScore);
  }

  if (activeMetricType.value === "min") {
    // 最低評価スコアの旅行（スコアが低い順に並べて改善点を確認）
    const minScore = Math.min(...list.map((r) => r.overallScore || 5));
    return list.filter((r) => r.overallScore <= Math.max(minScore, 4.3)).sort((a, b) => a.overallScore - b.overallScore);
  }

  // 平均値・中央値のときは全件を日付/スコア順に表示
  return list.sort((a, b) => b.overallScore - a.overallScore);
});

const metricHeaderKicker = computed(() => {
  if (activeMetricType.value === "max") return "🏆 ベスト体験ハイライト";
  if (activeMetricType.value === "min") return "⚠️ 改善ポイント・注意点";
  return "📋 評価済み旅行ログ";
});

const metricHeaderTitle = computed(() => {
  if (activeMetricType.value === "max") return `最高評価を獲得した旅行 (${filteredTripsForMetric.value.length}件)`;
  if (activeMetricType.value === "min") return `改善点・最低評価の旅行 (${filteredTripsForMetric.value.length}件)`;
  return `評価済みの旅行ログ一覧 (${filteredTripsForMetric.value.length}件)`;
});

// 計算ヘルパー関数
const calcMean = (arr) => {
  const valid = arr.filter((v) => typeof v === "number" && !isNaN(v) && v > 0);
  if (valid.length === 0) return 0;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
};

const calcMedian = (arr) => {
  const valid = arr.filter((v) => typeof v === "number" && !isNaN(v) && v > 0).sort((a, b) => a - b);
  if (valid.length === 0) return 0;
  const mid = Math.floor(valid.length / 2);
  return valid.length % 2 !== 0 ? valid[mid] : (valid[mid - 1] + valid[mid]) / 2;
};

const calcMax = (arr) => {
  const valid = arr.filter((v) => typeof v === "number" && !isNaN(v) && v > 0);
  if (valid.length === 0) return 0;
  return Math.max(...valid);
};

const calcMin = (arr) => {
  const valid = arr.filter((v) => typeof v === "number" && !isNaN(v) && v > 0);
  if (valid.length === 0) return 0;
  return Math.min(...valid);
};

// 総合評価統計
const overallStats = computed(() => {
  const scores = filteredReviews.value.map((r) => r.overallScore);
  const mean = calcMean(scores);
  const median = calcMedian(scores);
  const max = calcMax(scores);
  const min = calcMin(scores);

  return {
    mean: mean > 0 ? mean : 4.6,
    median: median > 0 ? median : 4.5,
    max: max > 0 ? max : 5.0,
    min: min > 0 ? min : 4.0,
  };
});

// 満足度（★4以上の割合）
const satisfactionRate = computed(() => {
  const scores = filteredReviews.value.map((r) => r.overallScore).filter((s) => s > 0);
  if (scores.length === 0) return 92;
  const satisfiedCount = scores.filter((s) => s >= 4.0).length;
  return Math.round((satisfiedCount / scores.length) * 100);
});

// カテゴリー別スコア詳細
const categoryMetrics = computed(() => {
  const list = [
    { key: "lodging", label: "宿泊・ホテル", icon: "🏨", defaultVal: 4.6 },
    { key: "food", label: "食事・グルメ", icon: "🍽️", defaultVal: 4.9 },
    { key: "sightseeing", label: "観光・名所", icon: "⛩️", defaultVal: 4.5 },
    { key: "activity", label: "アクティビティ", icon: "🏄", defaultVal: 4.3 },
    { key: "transport", label: "交通・移動", icon: "🚆", defaultVal: 4.1 },
  ];

  return list.map((c) => {
    const scores = filteredReviews.value.map((r) => r[c.key + "Score"]);
    const mean = calcMean(scores);
    const median = calcMedian(scores);
    const max = calcMax(scores);
    const min = calcMin(scores);

    return {
      key: c.key,
      label: c.label,
      icon: c.icon,
      mean: mean > 0 ? mean : c.defaultVal,
      median: median > 0 ? median : c.defaultVal,
      max: max > 0 ? max : 5.0,
      min: min > 0 ? min : 3.8,
    };
  });
});

const topCategoryLabel = computed(() => {
  const top = [...categoryMetrics.value].sort((a, b) => b.mean - a.mean)[0];
  return top ? `${top.label}（${top.mean.toFixed(1)}点）` : "グルメ・観光";
});

// 推奨旅行先一覧
const recommendedDestinations = [
  {
    id: "fukuoka-gourmet",
    title: "博多・天神 美食堪能の旅",
    tag: "グルメ最高評価おすすめ",
    description: "もつ鍋、屋台グルメ、糸島の絶景カフェを巡る、美味しいものづくしの人気コース。",
    days: 3,
    area: "福岡県",
    imageUrl: "https://images.unsplash.com/photo-1542051841857-5f90071e7989?w=600&auto=format&fit=crop&q=60",
  },
  {
    id: "kyoto-culture",
    title: "京都・宇治 伝統と風情を巡る歴史旅",
    tag: "観光高評価おすすめ",
    description: "静かな寺院巡りと抹茶スイーツ、情緒ある街並みをゆったり楽しむ充実のモデルプラン。",
    days: 2,
    area: "京都府",
    imageUrl: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=600&auto=format&fit=crop&q=60",
  },
  {
    id: "nagano-nature",
    title: "白馬・安曇野 大自然アクティビティ",
    tag: "アクティビティおすすめ",
    description: "北アルプスの絶景パノラマと爽快なアウトドア、温泉でリフレッシュする休日。",
    days: 3,
    area: "長野県",
    imageUrl: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=600&auto=format&fit=crop&q=60",
  },
];

const formatDateRange = (from, to) => {
  if (!from) return "日程未設定";
  const f = from.slice(0, 10);
  const t = to ? to.slice(0, 10) : f;
  return `${f} 〜 ${t}`;
};

// モーダル操作
const openNewManualModal = () => {
  activeEditingReview.value = null;
  showManualModal.value = true;
};

const openRateModalForShiori = (trip) => {
  activeEditingReview.value = {
    id: 'shiori_rev_' + trip.SHIORI_NOV,
    shioriId: trip.SHIORI_NOV,
    title: trip.SUBJECT_NMV || '旅行のしおり',
    date: formatDateRange(trip.TRIP_FROM, trip.TRIP_TO),
    members: (trip.members || []).map((m) => (typeof m === 'string' ? m : m.MEMBER_NAME)),
    overallScore: 5,
    lodgingScore: 4,
    foodScore: 5,
    sightseeingScore: 4,
    activityScore: 4,
    transportScore: 4,
    memo: '',
    isManual: false,
  };
  showManualModal.value = true;
};

const openEditModalForTrip = (trip) => {
  activeEditingReview.value = { ...trip };
  showManualModal.value = true;
};

// 評価の保存・更新ハンドラー
const onManualReviewSaved = (payload) => {
  const existingManualIdx = manualReviews.value.findIndex((m) => m.id === payload.id);
  if (existingManualIdx >= 0) {
    manualReviews.value[existingManualIdx] = payload;
  } else {
    const existingRevIdx = reviewsData.value.findIndex((r) => r.id === payload.id);
    if (existingRevIdx >= 0) {
      reviewsData.value[existingRevIdx] = payload;
    } else {
      manualReviews.value.unshift(payload);
    }
  }

  localStorage.setItem("rabiitrip_custom_trip_reviews", JSON.stringify(manualReviews.value));
};

const removeManualReview = (id) => {
  if (!confirm("この旅行評価ログを削除しますか？")) return;
  manualReviews.value = manualReviews.value.filter((m) => m.id !== id);
  localStorage.setItem("rabiitrip_custom_trip_reviews", JSON.stringify(manualReviews.value));
};

const openCreateModalWithPrefill = (plan) => {
  activePrefillData.value = {
    SUBJECT_NMV: plan.title,
    MEMO: plan.description,
  };
  showCreateModal.value = true;
};

const onShioriCreated = (newShiori) => {
  showCreateModal.value = false;
  if (newShiori?.SHIORI_NOV || newShiori?.shioriId) {
    router.push(`/shiori/${newShiori.SHIORI_NOV || newShiori.shioriId}`);
  } else {
    router.push("/mypage");
  }
};

onMounted(async () => {
  const savedManual = localStorage.getItem("rabiitrip_custom_trip_reviews");
  if (savedManual) {
    try {
      manualReviews.value = JSON.parse(savedManual);
    } catch (e) {
      console.error("Failed to parse custom reviews", e);
    }
  }

  if (authStore.token) {
    try {
      await shioriStore.fetchShioris();
      await tripReviewStore.fetchReviewSummaries();

      // 初期サンプルレビュー
      const sampleReviews = [
        {
          id: "sample_1",
          title: "箱根 温泉・美術館めぐり",
          date: "2026-05-10 〜 2026-05-12",
          overallScore: 4.8,
          lodgingScore: 4.9,
          foodScore: 4.8,
          sightseeingScore: 4.6,
          activityScore: 4.2,
          transportScore: 4.3,
          memo: "客室露天風呂と彫刻の森美術館が素晴らしかった。",
          members: uniqueMembers.value,
          isManual: false,
        },
      ];
      reviewsData.value = sampleReviews;
    } catch (e) {
      console.error("Failed to load dashboard data", e);
    }
  }
});
</script>

<style scoped>
.review-dashboard-page {
  min-height: 100vh;
  background: var(--color-background);
  color: var(--color-text);
}

.dashboard-container {
  max-width: 1040px;
  margin: 0 auto;
  padding: clamp(24px, 4vw, 40px) clamp(16px, 3vw, 32px);
  display: grid;
  gap: 2rem;
}

/* ── ヘッダー ── */
.dashboard-kicker {
  color: var(--color-primary-ink);
  font-size: 0.8125rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.25rem;
}

.dashboard-title {
  font-size: clamp(1.6rem, 3.5vw, 2.2rem);
  font-weight: 800;
  color: var(--color-text);
  margin: 0 0 0.5rem;
}

.dashboard-desc {
  color: var(--color-text-muted);
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0;
}

/* ── 共通セクション ── */
.section-title {
  font-size: 1.1875rem;
  font-weight: 800;
  color: var(--color-text);
  margin: 0;
}

.section-subtext {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin: 0.35rem 0 1.25rem;
  line-height: 1.5;
}

/* ── 未評価セクション ── */
.unreviewed-section {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
}

.section-badge-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.section-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.pulse-badge {
  background: #f97316;
  color: white;
  font-size: 0.6875rem;
  font-weight: 800;
  padding: 0.2rem 0.6rem;
  border-radius: var(--radius-full);
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
}

.section-badge-hint {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-primary-ink);
  background: var(--color-primary-light);
  padding: 0.2rem 0.6rem;
  border-radius: var(--radius-full);
}

.all-reviewed-box {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--color-primary-light);
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-lg);
  padding: 1rem 1.25rem;
  color: var(--color-primary-ink);
}

.reviewed-icon {
  font-size: 1.75rem;
}

.reviewed-text strong {
  display: block;
  font-size: 0.9375rem;
  color: var(--color-primary-ink);
}

.reviewed-text p {
  font-size: 0.8125rem;
  margin: 0.15rem 0 0;
  opacity: 0.9;
}

.unreviewed-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.unreviewed-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 0.875rem;
  transition: all var(--transition-fast);
}

.unreviewed-card:hover {
  border-color: var(--color-primary-ink);
  box-shadow: var(--shadow-sm);
}

.unreviewed-media {
  width: 60px;
  height: 60px;
  border-radius: var(--radius-md);
  overflow: hidden;
  flex-shrink: 0;
}

.trip-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.unreviewed-placeholder {
  width: 100%;
  height: 100%;
  background: var(--color-primary-light);
  color: var(--color-primary-ink);
  display: flex;
  align-items: center;
  justify-content: center;
}

.unreviewed-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.unreviewed-trip-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unreviewed-date {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.btn-rate-shiori {
  font-size: 0.75rem;
  padding: 0.35rem 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: var(--radius-full);
}

/* ── 統計セクション ── */
.stats-section {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.stats-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.stats-kicker {
  color: var(--color-primary-ink);
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
}

.header-actions-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.btn-add-manual {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 700;
  padding: 0.45rem 0.85rem;
  border-radius: var(--radius-md);
  border: 1.5px solid var(--color-primary-ink);
  color: var(--color-primary-ink);
  background: var(--color-primary-light);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-add-manual:hover {
  background: var(--color-primary-ink);
  color: white;
}

.member-filter-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 0.35rem 0.75rem;
}

.filter-label {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.member-select {
  border: none;
  background: transparent;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-text);
  cursor: pointer;
  outline: none;
}

/* ── KPIグリッド ── */
.metrics-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
}

.kpi-card {
  background: var(--color-background);
  border: 1.5px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.kpi-card:hover {
  border-color: var(--color-primary-ink);
  transform: translateY(-2px);
}

.kpi-card.is-selected {
  background: var(--color-primary-light);
  border-color: var(--color-primary-ink);
  box-shadow: 0 0 0 2px var(--color-primary);
}

.highlight-card-gold:hover {
  border-color: #d97706;
}

.highlight-card-warn:hover {
  border-color: #ef4444;
}

.kpi-label {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--color-text-muted);
}

.kpi-value {
  display: flex;
  align-items: baseline;
  gap: 0.2rem;
}

.kpi-value strong {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--color-text);
  line-height: 1;
}

.kpi-value.highlight-gold strong {
  color: #d97706;
}

.kpi-max {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  font-weight: 700;
}

.kpi-caption {
  font-size: 0.6875rem;
  color: var(--color-text-muted);
}

.kpi-meta-card {
  cursor: default;
}
.kpi-meta-card:hover {
  border-color: var(--color-border);
  transform: none;
}

/* ── 3. 指標連動：該当する旅行・しおりカード一覧 ── */
.active-metric-trips-panel {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.metric-trips-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.metric-trips-kicker {
  font-size: 0.6875rem;
  font-weight: 800;
  color: var(--color-primary-ink);
  text-transform: uppercase;
  display: block;
}

.metric-trips-title {
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--color-text);
  margin: 0.15rem 0 0;
}

.metric-trips-desc {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin: 0.15rem 0 0;
}

.metric-trips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.metric-trip-card {
  background: var(--color-surface);
  border: 1.5px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.metric-trip-card:hover {
  border-color: var(--color-primary-ink);
  box-shadow: var(--shadow-sm);
  transform: translateY(-2px);
}

.metric-trip-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.metric-trip-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.metric-trip-name {
  font-size: 0.9375rem;
  font-weight: 800;
  color: var(--color-text);
}

.metric-trip-date {
  font-size: 0.6875rem;
  color: var(--color-text-muted);
}

.badge-source-manual {
  font-size: 0.625rem;
  font-weight: 700;
  color: #0284c7;
  background: #e0f2fe;
  padding: 0.1rem 0.4rem;
  border-radius: var(--radius-sm);
  width: fit-content;
}

.badge-source-shiori {
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--color-primary-ink);
  background: var(--color-primary-light);
  padding: 0.1rem 0.4rem;
  border-radius: var(--radius-sm);
  width: fit-content;
}

.metric-trip-score-badge {
  font-size: 0.8125rem;
  font-weight: 800;
  color: var(--color-review);
  background: var(--color-background);
  border: 1px solid var(--color-border);
  padding: 0.2rem 0.55rem;
  border-radius: var(--radius-full);
  white-space: nowrap;
}

.metric-trip-score-badge.is-gold {
  color: #b45309;
  background: #fef3c7;
  border-color: #fde68a;
}

.metric-trip-memo {
  font-size: 0.75rem;
  color: var(--color-text);
  background: var(--color-background);
  padding: 0.4rem 0.6rem;
  border-radius: var(--radius-sm);
  margin: 0;
  line-height: 1.4;
}

.metric-trip-categories {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.trip-cat-pill {
  font-size: 0.6875rem;
  background: var(--color-background);
  color: var(--color-text-muted);
  padding: 0.15rem 0.4rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
}

.metric-trip-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid var(--color-border-light);
}

.btn-edit-trip-score {
  background: none;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 0.3rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-primary-ink);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-edit-trip-score:hover {
  background: var(--color-primary-light);
  border-color: var(--color-primary-ink);
}

.btn-view-shiori-link {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-text-muted);
  text-decoration: none;
  transition: all var(--transition-fast);
}

.btn-view-shiori-link:hover {
  color: var(--color-primary-ink);
}

.empty-metric-trips {
  padding: 2rem;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

/* ── カテゴリー別スコア詳細カード ── */
.category-detail-card {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.category-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.category-card-title {
  font-size: 1rem;
  font-weight: 800;
  color: var(--color-text);
  margin: 0;
}

.category-card-desc {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin: 0.15rem 0 0;
}

.category-card-desc strong {
  color: var(--color-primary-ink);
}

.metric-switch-pills {
  display: flex;
  gap: 0.35rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  padding: 0.2rem;
}

.metric-pill-btn {
  border: none;
  background: transparent;
  padding: 0.3rem 0.65rem;
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.metric-pill-btn.active {
  background: var(--color-primary-ink);
  color: white;
}

.category-bars-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.category-stat-row {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cat-stat-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.cat-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-text);
}

.cat-score-bold {
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--color-text);
}

.cat-score-bold small {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-muted);
}

.stat-bar-track {
  width: 100%;
  height: 6px;
  background: var(--color-border);
  border-radius: 999px;
  overflow: hidden;
}

.stat-bar-fill {
  height: 100%;
  background: var(--color-primary-ink);
  border-radius: 999px;
  transition: width 0.4s ease;
}

.cat-stat-sub-info {
  display: flex;
  gap: 0.75rem;
  font-size: 0.6875rem;
  color: var(--color-text-muted);
  font-weight: 600;
}

/* ── 手動学習ログ一覧 ── */
.manual-reviews-section {
  border-top: 1px solid var(--color-border);
  padding-top: 1.25rem;
}

.manual-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.manual-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-text);
}

.manual-hint {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.manual-reviews-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
}

.manual-review-item {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 0.75rem 0.875rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.manual-review-item:hover {
  border-color: var(--color-primary-ink);
  box-shadow: var(--shadow-sm);
}

.manual-item-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.manual-item-top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.manual-item-title {
  font-size: 0.875rem;
  color: var(--color-text);
}

.manual-item-date {
  font-size: 0.6875rem;
  color: var(--color-text-muted);
}

.manual-item-score {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--color-review);
  background: var(--color-surface);
  padding: 0.1rem 0.4rem;
  border-radius: var(--radius-full);
  border: 1px solid var(--color-border);
}

.manual-item-memo {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin: 0;
  line-height: 1.4;
}

.manual-item-tags {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
  margin-top: 0.15rem;
}

.cat-mini-tag {
  font-size: 0.6875rem;
  background: var(--color-surface);
  color: var(--color-text-muted);
  padding: 0.1rem 0.35rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
}

.manual-item-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.btn-icon-edit {
  background: none;
  border: none;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0.2rem;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
}

.btn-icon-edit:hover {
  background: var(--color-surface);
}

.btn-delete-manual {
  background: none;
  border: none;
  font-size: 1.1rem;
  line-height: 1;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 0.1rem 0.3rem;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
}

.btn-delete-manual:hover {
  color: var(--color-danger);
  background: var(--color-danger-light);
}

/* ── 推奨旅行先 ── */
.recommend-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.recommend-kicker {
  color: var(--color-primary-ink);
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: block;
}

.recommend-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

.recommend-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  transition: all var(--transition-fast);
}

.recommend-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.recommend-image-wrap {
  position: relative;
  height: 160px;
  overflow: hidden;
}

.recommend-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.recommend-card:hover .recommend-img {
  transform: scale(1.04);
}

.recommend-tag {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: rgba(18, 33, 52, 0.75);
  backdrop-filter: blur(4px);
  color: #ffffff;
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.25rem 0.625rem;
  border-radius: var(--radius-full);
}

.recommend-body {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
}

.recommend-plan-title {
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--color-text);
  margin: 0;
}

.recommend-plan-desc {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  line-height: 1.5;
  margin: 0;
  flex: 1;
}

.recommend-meta {
  display: flex;
  gap: 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-muted);
}

.btn-create-from-rec {
  margin-top: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-weight: 700;
}

@media (max-width: 767px) {
  .dashboard-container {
    padding: 1rem 0.75rem calc(5rem + env(safe-area-inset-bottom, 0));
    gap: 1.25rem;
  }

  .unreviewed-section,
  .stats-section,
  .recommend-card {
    padding: 1rem;
    border-radius: var(--radius-lg);
  }

  .metrics-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }

  .kpi-card {
    padding: 0.75rem;
  }

  .kpi-value strong {
    font-size: 1.25rem;
  }

  .metric-trips-grid,
  .recommend-grid,
  .category-bars-grid,
  .manual-reviews-list {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
}
</style>
